import BookingSummary from '@components/BookingSummary';
import { Button } from '@drdropin-tech/theseus';
import useAnalytics from '@hooks/useAnalytics';
import {
  isPhoneNumberGoodEnough,
  useBannerStore,
  useNavigationStore,
  usePersonalDataStore,
  useServiceTreeStore,
} from '@packages/shared';
import { Actions, PatientInfo } from '@packages/types';
import {
  CustomPhoneInput,
  InputError,
  StepWrapper,
  TermsOfService,
} from '@packages/ui-web';
import classNames from 'classnames';
import Cleave from 'cleave.js/react';
import { isAfter, isBefore, isValid, parse } from 'date-fns';
import { nb } from 'date-fns/locale';
import { type CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import {
  Controller,
  DeepMap,
  FieldError,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

export default function SelectPersonalDetailsFormStep() {
  const setEventProps = useAnalytics();
  const { t } = useTranslation(['booking', 'common']);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>('NO');

  // Close the feedback form banner, in case it was opened in the previous step.
  const closeBanner = useBannerStore((state) => state.closeBanner);
  useEffect(() => {
    closeBanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { advanceStep, queries } = useNavigationStore((state) => ({
    advanceStep: state.advanceStep,
    queries: state.queries,
  }));

  const { personalDetails, setPersonalDetails } = usePersonalDataStore()(
    (state) => ({
      personalDetails: state.personalDetails,
      setPersonalDetails: state.setPersonalDetails,
    }),
  );

  const { serviceMap } = useServiceTreeStore((state) => ({
    serviceMap: state.serviceMap,
  }));

  const [acceptedTerms, setAcceptedTerms] = useState(
    queries.partner !== 'storebrand',
  );

  const serviceFromMap = queries.service
    ? serviceMap.get(queries.service)
    : undefined;

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      ...personalDetails,
    },
  });

  const onSubmit: SubmitHandler<PatientInfo> = ({
    name,
    birthdate,
    description,
    phone,
  }) => {
    const partnerInfo = serviceFromMap?.membershipCampaignId
      ? {
          partner: queries.partner,
          partnerId: queries.partnerId,
          membershipCampaignId: serviceFromMap?.membershipCampaignId,
        }
      : null;
    setPersonalDetails({
      name: name?.trim(),
      birthdate,
      phone,
      description,
      countryCode: `+${getCountryCallingCode(selectedCountry)}`,
      selectedCountry,
      ...partnerInfo,
    });
    setEventProps({
      event: {
        action: Actions.SelectPersonalDetails,
        vertical: queries.vertical,
        speciality: queries.speciality,
        service: queries.service,
        city: queries.city,
        clinic: queries.clinicId,
      },
    });
    advanceStep();
  };

  const onError = (
    errors: DeepMap<Omit<PatientInfo, 'medicalIssue'>, FieldError>,
  ) => console.log(errors);

  //#region Validation
  const validDate = (date?: string): [boolean, string] => {
    if (!date) return [false, 'min_length'];
    const parsedDate = parse(date, 'dd/MM/yyyy', new Date(), { locale: nb });
    if (parsedDate) {
      const validDate = isValid(parsedDate);
      if (!validDate) {
        return [false, 'min_length'];
      }

      const isBeforeToday = isBefore(parsedDate, new Date());
      if (!isBeforeToday) {
        setEventProps({
          event: {
            action: Actions.FutureDateError,
          },
          target: {
            posthog: true,
          },
        });
        return [false, 'before_today'];
      }

      const isAfterThe20thCentury = isAfter(parsedDate, new Date(1900, 0, 1));
      if (!isAfterThe20thCentury) {
        setEventProps({
          event: {
            action: Actions.PastDateError,
          },
          target: {
            posthog: true,
          },
        });
        return [false, 'too_old'];
      }
      return [true, ''];
    }

    return [true, ''];
  };

  const changeSelectedCountry = (country: CountryCode) => {
    setSelectedCountry(country);
  };

  const validPhoneNumber = (input: string, selected: CountryCode) => {
    if (input && selected) {
      return isPhoneNumberGoodEnough(input, selected);
    }

    return false;
  };
  //#endregion

  const validatePatientName = (name: string) => {
    const nameParts = name.trim().split(/\s+/);

    // check for at least two names (first and last)
    if (nameParts.length < 2) {
      return t('steps.personal_details.input.error.name.last_name');
    }

    // check for minimum length of first name
    if (nameParts[0].length < 2) {
      return t('steps.personal_details.input.error.name.first_name_length');
    }

    // check for minimum length of last name
    for (const part of nameParts) {
      if (part.length < 2) {
        return t('steps.personal_details.input.error.name.last_name_length');
      }
    }

    return true;
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="mx-auto max-w-[446px] pb-20"
    >
      <StepWrapper
        title={t('steps.personal_details.title', { ns: 'booking' })}
        className="gap-6"
        stepWidth="max-w-[512px]"
      >
        <BookingSummary
          hasClinic={true}
          hasTimeStamp={true}
          practitionerId={queries.practitionerId}
          currentStep="selectPersonalDetails"
        />

        <div className="flex w-full flex-col gap-7">
          <p className="text-sm italic">
            {t('steps.personal_details.required_legend', { ns: 'booking' })}
          </p>
          {/* NAME INPUT START */}
          <Controller
            render={({
              fieldState: { isDirty },
              field: { value, onChange, name, onBlur, ref },
            }) => {
              return (
                <div className="flex w-full flex-col gap-2">
                  <label htmlFor={name}>
                    {t('steps.personal_details.input.label.name', {
                      ns: 'booking',
                    })}{' '}
                    <span aria-hidden="true">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id={name}
                      name={name}
                      value={value ?? ''}
                      onChange={(e) => {
                        onChange(e);
                        return e.target.value.replace(/\d/g, '');
                      }}
                      type="text"
                      required
                      aria-required="true"
                      className={classNames(
                        { 'focus-input-error': errors.name?.message },
                        'input border-accent focus-input w-full rounded-lg border-2 text-black',
                      )}
                      placeholder={t(
                        'steps.personal_details.input.placeholder.name',
                      )}
                      onBlur={onBlur}
                      ref={ref}
                      maxLength={100}
                      data-testid={name}
                      autoFocus={true}
                      autoComplete="name"
                    />

                    <InputError
                      isDirty={isDirty}
                      errors={errors.name as FieldError}
                    />
                  </div>
                  {errors.name?.message && (
                    <div className="rounded-lg bg-[#FFE2E0] p-3 text-xs leading-6 text-black">
                      {errors?.name?.message as string}
                    </div>
                  )}
                </div>
              );
            }}
            control={control}
            name="name"
            rules={{
              required: {
                value: true,
                message: t('steps.personal_details.input.error.name.required'),
              },
              pattern: /[A-Za-z]/,
              maxLength: {
                value: 100,
                message: t(
                  'steps.personal_details.input.error.name.max_length',
                ),
              },
              validate: (value) => {
                if (!value) return true;
                return validatePatientName(value);
              },
            }}
          />
          {/* NAME INPUT END */}
          {/* DATE OF BIRTH INPUT START */}
          <Controller
            render={({
              fieldState: { isDirty },
              field: { value, name, onChange, onBlur, ref },
            }) => {
              return (
                <div className="flex w-full flex-col gap-2">
                  <label htmlFor={name}>
                    {t('steps.personal_details.input.label.patient_birthdate')}{' '}
                    <span aria-hidden="true">*</span>
                  </label>
                  <div className="relative">
                    <Cleave
                      id={name}
                      options={{ date: true, datePattern: ['d', 'm', 'Y'] }}
                      className={classNames(
                        { 'focus-input-error': errors.birthdate?.message },
                        'input border-accent focus-input w-full rounded-lg border-2 text-black',
                      )}
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                      }}
                      name={name}
                      required
                      aria-required="true"
                      onBlur={onBlur}
                      placeholder={t(
                        'steps.personal_details.input.placeholder.patient_birthdate',
                      )}
                      htmlRef={ref}
                      onInit={() => ''}
                      inputMode="numeric"
                      data-testid={name}
                      autoComplete="bday"
                    />

                    <InputError
                      isDirty={isDirty}
                      errors={errors.birthdate as FieldError}
                    />
                  </div>
                  {errors.birthdate && (
                    <div className="rounded-lg bg-[#FFE2E0] p-3 text-xs leading-6 text-black">
                      {String(errors.birthdate?.message)}
                    </div>
                  )}
                </div>
              );
            }}
            name="birthdate"
            control={control}
            rules={{
              required: {
                value: true,
                message: t(
                  'steps.personal_details.input.error.birthdate.required',
                ),
              },
              minLength: {
                value: 10,
                message: t(
                  'steps.personal_details.input.error.birthdate.min_length',
                ),
              },
              validate: (value) => {
                const [isValid, errorKey] = validDate(value);
                if (!isValid && errorKey)
                  return t(
                    `steps.personal_details.input.error.birthdate.${errorKey}`,
                  );
                return isValid;
              },
            }}
          />
          {/* DATE OF BIRTH INPUT END */}
          {/* PHONE NUMBER INPUT START */}
          <Controller
            render={({
              fieldState: { isDirty },
              field: { value, name, onBlur, onChange },
            }) => {
              return (
                <div className="flex w-full flex-col gap-2">
                  <CustomPhoneInput
                    name={name}
                    label={t(
                      'steps.personal_details.input.label.phone_number.label',
                    )}
                    countryCodeLabel={t(
                      'steps.personal_details.input.label.phone_number.country_code',
                    )}
                    numberLabel={t(
                      'steps.personal_details.input.label.phone_number.number',
                    )}
                    defaultCountry={selectedCountry}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    changeSelectedCountry={changeSelectedCountry}
                    selectedCountry={selectedCountry as CountryCode}
                    isDirty={isDirty}
                    errors={errors.phone as FieldError}
                    i18nFunction={t}
                    required
                  />
                </div>
              );
            }}
            name="phone"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('steps.personal_details.input.error.phone.required'),
              },
              validate: {
                validPhoneNumber: (input) => {
                  const isValid =
                    input && !validPhoneNumber(input, selectedCountry)
                      ? t('steps.personal_details.input.error.phone.min_length')
                      : true;
                  return isValid;
                },
              },
            }}
          />
          {/* PHONE NUMBER INPUT END */}
          {/* DESCRIPTION INPUT START */}
          <Controller
            render={({
              fieldState: { isDirty },
              field: { value, name, onBlur, onChange, ref },
            }) => (
              <div className={'relative flex w-full flex-col gap-2'}>
                <label htmlFor={name}>
                  {t('steps.personal_details.input.label.description', {
                    ns: 'booking',
                  })}{' '}
                  <span aria-hidden="true">*</span>
                </label>
                <div className="relative flex">
                  <textarea
                    id={name}
                    name={name}
                    value={value}
                    required
                    aria-required="true"
                    className={classNames(
                      { 'focus-input-error': errors.description?.message },
                      'textarea border-accent focus-input min-h-[140px] w-full rounded-lg border-2 text-black',
                    )}
                    placeholder={t(
                      'steps.personal_details.input.placeholder.text_area',
                    )}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    maxLength={500}
                    data-testid={name}
                  />
                  <InputError
                    isDirty={isDirty}
                    errors={errors.description as FieldError}
                  />
                </div>
                {errors.description && (
                  <div className="rounded-lg bg-[#FFE2E0] p-3 text-xs leading-6 text-black">
                    {errors.description.message as string}
                  </div>
                )}
              </div>
            )}
            name="description"
            control={control}
            rules={{
              required: {
                value: true,
                message: t(
                  'steps.personal_details.input.error.description.required',
                ),
              },
              maxLength: {
                value: 500,
                message: t(
                  'steps.personal_details.input.error.description.max_length',
                ),
              },
              validate: {
                minCharsIgnoringSpaces: (value) => {
                  // let required handle actual empty input
                  if (!value) return true;

                  const trimmed = value.trim();

                  // user typed only spaces -> show min_length (not required)
                  if (!trimmed) {
                    return t(
                      'steps.personal_details.input.error.description.min_length',
                    );
                  }
                  return trimmed.length >= 3
                    ? true
                    : t(
                        'steps.personal_details.input.error.description.min_length',
                      );
                },
              },
            }}
          />
          {/* DESCRIPTION INPUT END */}

          {queries.partner === 'storebrand' && (
            <TermsOfService
              checked={acceptedTerms}
              onChange={setAcceptedTerms}
              i18nFunction={t}
            />
          )}

          <div className="w-full pt-8">
            <Button
              type="submit"
              appearance="accent"
              disabled={!acceptedTerms}
              className="w-full font-normal normal-case"
              data-testid="submit"
            >
              {t('next', { ns: 'common' })}
            </Button>
          </div>
        </div>
      </StepWrapper>
    </form>
  );
}
